import React from 'react';

import { Book } from '@/utils/bookRouting';

interface HeaderSearchResultsProps {
  id: string;
  query: string;
  results: Book[];
  total: number;
  /** True while the shown results don't yet answer `query`. */
  isPending: boolean;
  error: string | null;
  activeIndex: number;
  isDarkMode: boolean;
  onSelect: (book: Book) => void;
  onSeeAll: () => void;
  onActiveIndexChange: (index: number) => void;
  className?: string;
}

export const headerSearchOptionId = (listboxId: string, index: number) => `${listboxId}-option-${index}`;

const HeaderSearchResults: React.FC<HeaderSearchResultsProps> = ({
  id,
  query,
  results,
  total,
  isPending,
  error,
  activeIndex,
  isDarkMode,
  onSelect,
  onSeeAll,
  onActiveIndexChange,
  className,
}) => {
  if (!query) {
    return null;
  }

  const panelClasses = isDarkMode
    ? 'bg-gray-800 border-gray-700 text-gray-200'
    : 'bg-white border-gray-300 text-theme-primary-light';
  // Same inset, rounded highlight as BookActionsMenu's items.
  const activeRowClasses = isDarkMode ? 'bg-gray-700' : 'bg-gray-200/50';
  const hoverClasses = isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200/50';
  const dividerClasses = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  let status: string | null = null;
  if (error) {
    status = error;
  } else if (results.length === 0) {
    status = isPending ? 'Searching…' : `No books matched “${query}”`;
  }

  return (
    <div className={`overflow-hidden rounded-lg border text-left shadow-xl ${panelClasses} ${className ?? ''}`}>
      {status ? (
        <p role="status" className="px-4 py-3 text-sm opacity-75">
          {status}
        </p>
      ) : (
        <>
          <ul
            id={id}
            role="listbox"
            aria-label="Search results"
            // Hover drives the highlight, so it must not outlast the pointer.
            onMouseLeave={() => onActiveIndexChange(-1)}
            className={`max-h-[min(60vh,26rem)] overflow-y-auto p-1.5 transition-opacity ${
              isPending ? 'opacity-60' : ''
            }`}
          >
            {results.map((book, index) => (
              <li
                key={book.id}
                id={headerSearchOptionId(id, index)}
                role="option"
                aria-selected={index === activeIndex}
                // Keep focus in the input so typing and arrow keys keep working.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => onActiveIndexChange(index)}
                onClick={() => onSelect(book)}
                className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 transition-colors ${
                  index === activeIndex ? activeRowClasses : ''
                }`}
              >
                {/* Square crop anchored to the bottom, as in PersistentAudioPlayer. */}
                {book.coverUrl ? (
                  <img
                    src={book.coverUrl}
                    alt=""
                    className="aspect-square w-12 shrink-0 rounded-sm object-cover object-bottom"
                  />
                ) : (
                  <div className={`aspect-square w-12 shrink-0 rounded-sm ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                )}
                <div className="min-w-0">
                  <p className="truncate font-medium">{book.title}</p>
                  <p className="truncate text-sm opacity-70">{book.author}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className={`border-t p-1.5 ${dividerClasses}`}>
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onSeeAll}
              className={`w-full cursor-pointer rounded-md px-3 py-2 text-left text-sm font-semibold transition-colors ${hoverClasses}`}
            >
              {total === 1 ? 'See result in library' : `See all ${total} results`}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderSearchResults;
